import { Link } from "react-router-dom";
import { List, Ruler } from "lucide-react";
import { Card, Chip, ChipRow, DraftNumber, Field, Label } from "@/components/kit";
import { METRIC, toShown, fromShown } from "@/lib/units";
import { useT } from "@/lib/i18n";
import { CAPACITIES, UST_DATABASE, capacityFromDims, endsLabel, endsShort, formatGal, lengthFor, lengthInches, tankKey } from "@/lib/tank";

// Inside diameter + end-to-end length; gallons come from the horizontal-cylinder formula.
function CustomTank({ s, patch }) {
  const t = useT();
  const len = s.customLength > 0 ? s.customLength : Math.round(lengthInches(s.capacity, s.diameter, s.ends));
  const set = (d, l, ends) => {
    const cap = capacityFromDims(d, l, ends);
    patch(cap >= 500 ? { diameter: d, customLength: l, ends, capacity: cap, customDims: true } : { customLength: l });
  };
  const bad = capacityFromDims(s.diameter, len, s.ends) < 500;
  return (
    <div className="inputs" data-testid="custom-tank">
      <div className="two-col">
        <Field label={METRIC ? t("Inside diameter (cm)") : t("Inside diameter (in)")}><DraftNumber testID="custom-width" decimal value={toShown(s.diameter, "in")} onValue={(v) => { const d = fromShown(v, "in"); if (d >= 30) set(d, len, s.ends); }} /></Field>
        <Field label={METRIC ? t("Length end to end (cm)") : t("Length end to end (in)")}><DraftNumber testID="custom-length" decimal value={toShown(len, "in")} onValue={(v) => { const l = fromShown(v, "in"); if (l > 0) set(s.diameter, l, s.ends); }} /></Field>
      </div>
      <div className="opt-row">
        <Chip testID="custom-ends-flat" active={s.ends === "flat"} onPress={() => set(s.diameter, len, "flat")}>{t("Flat ends")}</Chip>
        <Chip testID="custom-ends-dome" active={s.ends === "dome"} onPress={() => set(s.diameter, len, "dome")}>{t("Dished / rounded ends")}</Chip>
      </div>
      {bad
        ? <div className="form-error" data-testid="custom-error">{t("Length must be longer than the diameter and hold at least 500 gal.")}</div>
        : <div className="field-hint mono" data-testid="custom-gallons">{t("Holds {g} gal", { g: formatGal(s.capacity) })}</div>}
    </div>
  );
}

const COMMON_CAPS = [4000, 6000, 8000, 10000, 12000, 15000, 20000, 30000];

export function TankPicker({ s, patch, custom, setCustom, compact = false }) {
  const t = useT();
  const specs = UST_DATABASE.filter((t) => t.capacity === s.capacity);
  const other = custom || (compact && !COMMON_CAPS.includes(s.capacity));
  const pickCap = (cap) => {
    setCustom(false);
    const first = UST_DATABASE.find((x) => x.capacity === cap);
    patch({ capacity: cap, diameter: first?.diameter ?? s.diameter, ends: first?.ends ?? s.ends, customDims: false, customLength: 0 });
  };
  return (
    <Card testID="tank-card">
      <Label>{t("Tank")}</Label>
      <ChipRow testID="capacity-row" wrap={compact}>
        {(compact ? COMMON_CAPS : CAPACITIES).map((c) => (
          <Chip key={c} testID={`cap-${c}`} active={!other && s.capacity === c} onPress={() => pickCap(c)}>{formatGal(c)} gal</Chip>
        ))}
        <Chip testID="cap-other" active={other} onPress={() => setCustom(true)}>{t(compact ? "Other size" : "Other")}</Chip>
      </ChipRow>
      {!compact && !custom && (
        <button type="button" className="ghost-btn custom-tank-btn" data-testid="cap-custom" onClick={() => setCustom(true)}>
          <Ruler size={16} />{t("Custom tank (diameter & length)")}
        </button>
      )}
      {!other && specs.length > 1 && (
        <div className="dia-row">
          <span className="field-hint" data-testid="tank-width-label">{t("Tank width")}</span>
          <ChipRow testID="diameter-row">
            {specs.map((x) => (
              <Chip key={tankKey(x)} testID={`dia-${x.diameter}-${x.ends}`}
                active={s.diameter === x.diameter && s.ends === x.ends}
                onPress={() => patch({ diameter: x.diameter, ends: x.ends, customDims: false, customLength: 0 })}>
                {x.diameter}" {t(endsShort(x.ends))}
              </Chip>
            ))}
          </ChipRow>
        </div>
      )}
      {other && <CustomTank s={s} patch={patch} />}
      {other && compact && <Link className="tank-sizes-link" to="/tanks" data-testid="other-size-tanks-link"><List size={14} />{t("See all tank sizes")}</Link>}
      <p className="note" data-testid="tank-length-note">
        {t("About {len} long · {ends}.", { len: lengthFor(s.capacity, s.diameter, s.ends), ends: t(endsLabel(s.ends)) })}
        {s.diameter > 120 && ` ${t("Deeper than a 10 ft stick.")}`}
      </p>
    </Card>
  );
}
